import { type ReactElement, useCallback, useMemo } from 'react';

import clsx from 'clsx';
import { YAxis } from 'recharts';
import { type AxisDomain } from 'recharts/types/util/types';

import styles from './Axis.module.scss';

export type YLabelProps = {
  y: number;
  index: number;
  minY: number;
  maxY: number;
};

export type YLabelFormatter = (props: YLabelProps) => string;

type UseYAxisOptions = {
  values: number[][];
  ticks?: number[];
  domain?: AxisDomain;
  width?: number;
  textType?: 'regular14';
  formatter?: YLabelFormatter;
};

const DEFAULT_DOMAIN: AxisDomain = ['auto', 'auto'];

// хук, а не компонент, потому что recharts не рендерит кастомные обертки их компонентов
export function useYAxis(options: UseYAxisOptions): ReactElement {
  const { values, ticks, domain: propsDomain = DEFAULT_DOMAIN, width, textType = 'regular14', formatter } = options;

  const { min: minY, max: maxY } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    values.forEach((values) => {
      values.forEach((value) => {
        min = Math.min(min, value);
        max = Math.max(max, value);
      });
    });

    return { min, max };
  }, [values]);

  const domain = useMemo(() => {
    if (ticks && ticks.length > 1) {
      return [ticks[0], ticks[ticks.length - 1]];
    }

    return propsDomain;
  }, [propsDomain, ticks]);

  const getYLabel = useCallback(
    (value: number, index: number): string => {
      return formatter?.({ y: value, index, minY, maxY }) ?? String(value);
    },
    [formatter, maxY, minY]
  );

  return useMemo(
    () => (
      <YAxis
        className={clsx(styles.root, styles[`type_${textType}`])}
        ticks={ticks}
        domain={domain}
        tickFormatter={getYLabel}
        width={width}
      />
    ),
    [domain, getYLabel, textType, ticks, width]
  );
}
